<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <b-table
        ref="refPhysicianCompanyListTable"
        class="position-relative"
        :items="fetchPhysicianCompanies"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="Nenhum registro correspondente encontrado"
      >
        <template #cell(company)="{ item }">
          <strong>{{ item.name }}</strong>
          <div>{{ item.cnpj }}</div>
          <div v-if="item.partner_qualification && item.partner_qualification.id">
            {{ item.partner_qualification.id }} - {{ item.partner_qualification.description }}
          </div>
        </template>
        <template #cell(validation_status)="{ item }">
          <b-badge
            pill
            :variant="validationStatusMap[item.validation_status || 'pending'].variant"
          >
            {{ validationStatusMap[item.validation_status || 'pending'].label }}
          </b-badge>
        </template>
        <template #cell(motive_need_validate_manually)="{ item }">
          {{ item.motive_need_validate_manually || '-' }}
        </template>
        <template #cell(attorney)="{ item }">
          <b-link
            v-if="item.attorney && item.attorney.file_url"
            :href="item.attorney.file_url"
            target="_blank"
            rel="noopener noreferrer"
            :title="item.attorney.file_name"
          >
            <feather-icon icon="FileTextIcon" />
            <span class="align-middle ml-50">Visualizar</span>
          </b-link>
          <span v-else>-</span>
        </template>
        <template #cell(actions)="{ item }">
          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item @click="openValidationModal(item)">
              <feather-icon icon="CheckCircleIcon" />
              <span class="align-middle ml-50">Aprovar/Negar</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <b-modal
      id="modal-validate-physician-company"
      ref="validationModal"
      title="Aprovar/Negar CNPJ"
      hide-footer
      size="md"
      centered
    >
      <b-overlay
        :show="saving"
        variant="light"
        opacity="0.3"
        blur="5px"
        rounded="sm"
      >
        <div class="mb-1">
          <strong>{{ validationData.name }}</strong>
          <div>{{ validationData.cnpj }}</div>
        </div>
        <b-form-group
          label="Motivo"
          label-for="justification"
        >
          <b-form-textarea
            id="justification"
            v-model="validationData.justification"
            name="justification"
            placeholder="Descreva o motivo..."
            rows="3"
            :state="errors.justification ? false : null"
          />
          <small
            v-if="errors.justification"
            class="text-danger"
          >{{ errors.justification }}</small>
        </b-form-group>
        <b-form-group
          v-if="validationData.requires_attorney_expiration"
          label="Vencimento da procuração"
          label-for="attorney-expires-at"
        >
          <b-form-datepicker
            id="attorney-expires-at"
            v-model="validationData.attorney_expires_at"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            locale="pt-BR"
            label-help=""
            placeholder="Selecione a data"
            :min="minAttorneyExpiresAt"
            :state="errors.attorney_expires_at ? false : null"
          />
          <small class="text-muted d-block">Obrigatório para aprovar.</small>
          <small
            v-if="errors.attorney_expires_at"
            class="text-danger"
          >{{ errors.attorney_expires_at }}</small>
        </b-form-group>
        <div class="text-right">
          <b-button
            variant="outline-danger"
            class="mr-1"
            @click="deny()"
          >
            Negar
          </b-button>
          <b-button
            variant="primary"
            @click="approve()"
          >
            Aprovar
          </b-button>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BBadge,
  BLink,
  BButton,
  BDropdown,
  BDropdownItem,
  BModal,
  BOverlay,
  BFormGroup,
  BFormDatepicker,
  BFormTextarea,
} from 'bootstrap-vue'
import store from '@/store'
import { onUnmounted, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { addDays, format } from 'date-fns'
import { getValidationErrors } from '@core/comp-functions/forms/form-error'
import physicianStoreModule from '../reportStoreModule'

export default {
  components: {
    BCard,
    BTable,
    BBadge,
    BLink,
    BButton,
    BDropdown,
    BDropdownItem,
    BModal,
    BOverlay,
    BFormGroup,
    BFormDatepicker,
    BFormTextarea,
  },
  setup() {
    const PHYSICIAN_APP_STORE_MODULE_NAME = 'app-physician'

    // Register module
    if (!store.hasModule(PHYSICIAN_APP_STORE_MODULE_NAME)) store.registerModule(PHYSICIAN_APP_STORE_MODULE_NAME, physicianStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(PHYSICIAN_APP_STORE_MODULE_NAME)) store.unregisterModule(PHYSICIAN_APP_STORE_MODULE_NAME)
    })

    const toast = useToast()

    const refPhysicianCompanyListTable = ref(null)

    const tableColumns = [
      { key: 'company', label: 'Empresa' },
      { key: 'validation_status', label: 'Ativação Manual?' },
      { key: 'motive_need_validate_manually', label: 'Motivo' },
      { key: 'attorney', label: 'Procuração' },
      { key: 'actions', label: 'Ações' },
    ]

    const validationStatusMap = {
      validated: { label: 'Validado', variant: 'light-success' },
      pending: { label: 'Aguardando aprovação', variant: 'light-warning' },
      denied: { label: 'Negado', variant: 'light-danger' },
    }

    const fetchPhysicianCompanies = (ctx, callback) => {
      store
        .dispatch('app-physician/fetchPendingValidationPhysicianCompanies')
        .then(response => {
          callback(response.data.data)
        })
        .catch(() => {
          callback([])
          toast({
            component: ToastificationContent,
            props: {
              title: 'Erro ao buscar',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const validationModal = ref(null)
    const saving = ref(false)
    const errors = ref({})
    const minAttorneyExpiresAt = ref(null)
    const validationData = ref({
      id: null,
      name: '',
      cnpj: '',
      justification: '',
      attorney_expires_at: null,
      requires_attorney_expiration: false,
    })

    const openValidationModal = item => {
      errors.value = {}
      // O backend exige vencimento posterior a hoje (regra after:today)
      minAttorneyExpiresAt.value = format(addDays(new Date(), 1), 'yyyy-MM-dd')
      validationData.value = {
        id: item.id,
        name: item.name,
        cnpj: item.cnpj,
        justification: '',
        attorney_expires_at: null,
        requires_attorney_expiration: item.requires_attorney_expiration,
      }
      validationModal.value.show()
    }

    const validate = isApproval => {
      const data = validationData.value
      const fieldErrors = {}
      if (!data.justification.trim()) {
        fieldErrors.justification = 'O campo motivo é obrigatório.'
      }
      if (isApproval && data.requires_attorney_expiration) {
        if (!data.attorney_expires_at) {
          fieldErrors.attorney_expires_at = 'Informe a data de vencimento da procuração.'
        } else if (data.attorney_expires_at < minAttorneyExpiresAt.value) {
          fieldErrors.attorney_expires_at = 'A data de vencimento da procuração deve ser posterior a hoje.'
        }
      }
      errors.value = fieldErrors
      return Object.keys(fieldErrors).length === 0
    }

    const getErrorMessage = error => {
      const fields = error.response && error.response.data && error.response.data.data
      if (fields && !Array.isArray(fields) && typeof fields === 'object') {
        const messages = Object.values(fields).reduce((all, value) => all.concat(value), [])
        if (messages.length) return messages.join(', ')
      }
      return getValidationErrors(error)
    }

    const submit = (action, payload, successText) => {
      saving.value = true
      store
        .dispatch(`app-physician/${action}`, payload)
        .then(() => {
          validationModal.value.hide()
          refPhysicianCompanyListTable.value.refresh()
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Sucesso',
              icon: 'CheckIcon',
              variant: 'success',
              text: successText,
            },
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Erro',
              icon: 'XOctagonIcon',
              variant: 'danger',
              text: getErrorMessage(error),
            },
          })
        })
        .finally(() => {
          saving.value = false
        })
    }

    const approve = () => {
      if (!validate(true)) return
      const data = validationData.value
      const payload = { id: data.id, justification: data.justification }
      if (data.requires_attorney_expiration) payload.attorney_expires_at = data.attorney_expires_at
      submit('approvePhysicianCompany', payload, 'CNPJ aprovado com sucesso')
    }

    const deny = () => {
      if (!validate(false)) return
      const data = validationData.value
      submit('denyPhysicianCompany', { id: data.id, justification: data.justification }, 'CNPJ negado com sucesso')
    }

    return {
      refPhysicianCompanyListTable,
      tableColumns,
      validationStatusMap,
      fetchPhysicianCompanies,
      validationModal,
      validationData,
      minAttorneyExpiresAt,
      errors,
      saving,
      openValidationModal,
      approve,
      deny,
    }
  },
}
</script>
